
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type GBufferTerrainShaderUniforms = {
	uProjectionMatrix: WebGLUniformLocation,
	uUnjitteredViewProjection: WebGLUniformLocation,
	uPreviousViewProjection: WebGLUniformLocation,
	uViewMatrix: WebGLUniformLocation,
	uModelViewMatrix: WebGLUniformLocation,
	uModelMatrix: WebGLUniformLocation,
	uAlbedoArray: WebGLUniformLocation,
	uNormalArray: WebGLUniformLocation,
	uRoughnessArray: WebGLUniformLocation,
	uSurfaceIndices: WebGLUniformLocation,
	uSurfaceWeight: WebGLUniformLocation,
	uIndexMapBoundsAndSize: WebGLUniformLocation,
	uSurfacePropsTex: WebGLUniformLocation,
	uDitherFade: WebGLUniformLocation
};

export type GBufferTerrainShaderAttributes = {
	aInstanceModelCol0: number,
	aInstanceModelCol1: number,
	aInstanceModelCol2: number,
	aInstanceModelCol3: number,
	aVertexPosition: number,
	aVertexNormal: number,
	vWorldPosition: number,
	vWorldNormal: number,
	vClipCurr: number,
	vClipPrev: number
};

export type GBufferTerrainShaderSettings = ('INSTANCED' | 'HAS_NORMAL_ARRAY' | 'HAS_ROUGHNESS_ARRAY' | 'CROSSFADE')[];

export class GBufferTerrainShader extends ShaderBase<GBufferTerrainShaderUniforms, GBufferTerrainShaderAttributes> {

    private static fragmentShaderSource = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
precision highp sampler2DArray;
precision highp sampler2D;
#else
precision mediump float;
precision mediump sampler2DArray;
precision mediump sampler2D;
#endif

layout(location = 0) out vec4 gAlbedo;
layout(location = 1) out vec4 gNormal;
layout(location = 2) out vec2 gMotion;

in vec4 vWorldPosition;
in vec3 vWorldNormal;
in vec4 vClipCurr;
in vec4 vClipPrev;

uniform sampler2DArray uAlbedoArray;

#ifdef NOODLE_HAS_NORMAL_ARRAY
uniform sampler2DArray uNormalArray;
#endif

#ifdef NOODLE_HAS_ROUGHNESS_ARRAY
uniform sampler2DArray uRoughnessArray;
#endif

/**
 * RG8 NEAREST index pair: R=layerA, G=layerB. Sampled manually via texelFetch
 * on the 4 surrounding texels so we can bilinear-interpolate in COLOR space.
 * The GPU's built-in LINEAR filtering can't be used on integer surface IDs
 * (bilinear of e.g. 3 and 7 yields 5, an unrelated surface).
 */
uniform sampler2D uSurfaceIndices;

/** R8 weight, also sampled manually so the blend runs in color space too. */
uniform sampler2D uSurfaceWeight;

/**
 * (worldMinX, worldMinZ, invWorldWidth, invWorldDepth) — projects world XZ to
 * normalised UV for both maps (same dimensions, same UV).
 */
uniform vec4 uIndexMapBoundsAndSize;

/**
 * 1×N RGBA32F lookup: texel i holds (tiling, roughnessFallback, 0, 0) for surface i.
 */
uniform sampler2D uSurfacePropsTex;

#ifdef NOODLE_CROSSFADE
uniform float uDitherFade;
const float bayer4x4[16] = float[](
	 0.0/16.0,  8.0/16.0,  2.0/16.0, 10.0/16.0,
	12.0/16.0,  4.0/16.0, 14.0/16.0,  6.0/16.0,
	 3.0/16.0, 11.0/16.0,  1.0/16.0,  9.0/16.0,
	15.0/16.0,  7.0/16.0, 13.0/16.0,  5.0/16.0
);
#endif

const float TRIPLANAR_POWER = 4.0;

vec3 triplanarWeights(vec3 N) {
	vec3 w = pow(abs(N), vec3(TRIPLANAR_POWER));
	float s = w.x + w.y + w.z;
	return w / max(s, 1e-5);
}

vec3 sampleTriplanarAlbedo(float layer, vec3 worldPos, vec3 triW, float tiling) {
	vec2 uvYZ = worldPos.zy * tiling;
	vec2 uvXZ = worldPos.xz * tiling;
	vec2 uvXY = worldPos.xy * tiling;
	vec3 aYZ = texture(uAlbedoArray, vec3(uvYZ, layer)).rgb;
	vec3 aXZ = texture(uAlbedoArray, vec3(uvXZ, layer)).rgb;
	vec3 aXY = texture(uAlbedoArray, vec3(uvXY, layer)).rgb;
	return aYZ * triW.x + aXZ * triW.y + aXY * triW.z;
}

#ifdef NOODLE_HAS_NORMAL_ARRAY
vec3 sampleTriplanarNormal(float layer, vec3 worldPos, vec3 N, vec3 triW, float tiling) {
	vec2 uvYZ = worldPos.zy * tiling;
	vec2 uvXZ = worldPos.xz * tiling;
	vec2 uvXY = worldPos.xy * tiling;
	vec3 nYZ = texture(uNormalArray, vec3(uvYZ, layer)).rgb * 2.0 - 1.0;
	vec3 nXZ = texture(uNormalArray, vec3(uvXZ, layer)).rgb * 2.0 - 1.0;
	vec3 nXY = texture(uNormalArray, vec3(uvXY, layer)).rgb * 2.0 - 1.0;
	vec3 wYZ = vec3(nYZ.z * sign(N.x), nYZ.y, nYZ.x);
	vec3 wXZ = vec3(nXZ.x, nXZ.z * sign(N.y), nXZ.y);
	vec3 wXY = vec3(nXY.x, nXY.y, nXY.z * sign(N.z));
	return normalize(wYZ * triW.x + wXZ * triW.y + wXY * triW.z);
}
#endif

#ifdef NOODLE_HAS_ROUGHNESS_ARRAY
float sampleTriplanarRoughness(float layer, vec3 worldPos, vec3 triW, float tiling) {
	vec2 uvYZ = worldPos.zy * tiling;
	vec2 uvXZ = worldPos.xz * tiling;
	vec2 uvXY = worldPos.xy * tiling;
	float rYZ = texture(uRoughnessArray, vec3(uvYZ, layer)).r;
	float rXZ = texture(uRoughnessArray, vec3(uvXZ, layer)).r;
	float rXY = texture(uRoughnessArray, vec3(uvXY, layer)).r;
	return rYZ * triW.x + rXZ * triW.y + rXY * triW.z;
}
#endif

struct TexelEval {
	vec3 albedo;
	vec3 normal;
	float roughness;
};

/**
 * Evaluate one index-map texel's blended surface attributes. Pulls
 * (idxA, idxB, weight) from texel \`coord\`, samples both surfaces, and blends.
 * Used by the slow-path 4-corner bilinear that runs on boundary fragments.
 */
TexelEval evalTexel(ivec2 coord, vec3 worldPos, vec3 N, vec3 triW) {
	vec2 idxRG = texelFetch(uSurfaceIndices, coord, 0).rg;
	float w = texelFetch(uSurfaceWeight, coord, 0).r;
	int idxA = int(idxRG.r * 255.0 + 0.5);
	int idxB = int(idxRG.g * 255.0 + 0.5);
	vec4 propsA = texelFetch(uSurfacePropsTex, ivec2(idxA, 0), 0);
	vec4 propsB = texelFetch(uSurfacePropsTex, ivec2(idxB, 0), 0);
	float tilingA = propsA.r;
	float tilingB = propsB.r;

	vec3 aA = sampleTriplanarAlbedo(float(idxA), worldPos, triW, tilingA);
	vec3 aB = sampleTriplanarAlbedo(float(idxB), worldPos, triW, tilingB);

	float roughA = propsA.g;
	float roughB = propsB.g;
#ifdef NOODLE_HAS_ROUGHNESS_ARRAY
	roughA = sampleTriplanarRoughness(float(idxA), worldPos, triW, tilingA);
	roughB = sampleTriplanarRoughness(float(idxB), worldPos, triW, tilingB);
#endif

	TexelEval r;
	r.albedo = mix(aA, aB, w);
	r.roughness = mix(roughA, roughB, w);
	r.normal = N;
#ifdef NOODLE_HAS_NORMAL_ARRAY
	vec3 nA = sampleTriplanarNormal(float(idxA), worldPos, N, triW, tilingA);
	vec3 nB = sampleTriplanarNormal(float(idxB), worldPos, N, triW, tilingB);
	r.normal = normalize(mix(nA, nB, w));
#endif
	return r;
}

void main(void) {
#ifdef NOODLE_CROSSFADE
	ivec2 pc = ivec2(gl_FragCoord.xy);
	float ditherThreshold = bayer4x4[(pc.x & 3) * 4 + (pc.y & 3)];
	if (uDitherFade >= 0.0) {
		if (ditherThreshold < uDitherFade) discard;
	} else {
		if (ditherThreshold >= -uDitherFade) discard;
	}
#endif

	vec3 N = normalize(vWorldNormal);
	vec3 triW = triplanarWeights(N);
	vec3 worldPos = vWorldPosition.xyz;

	vec2 imUV = (worldPos.xz - uIndexMapBoundsAndSize.xy) * uIndexMapBoundsAndSize.zw;

	// Manually bilinear-sample the 4 surrounding index-map texels so the blend
	// runs in COLOR space, not in (integer index, weight) space. With built-in
	// LINEAR weight + NEAREST indices, the weight continuously crosses the
	// texel boundary while the (idxA, idxB) pair flips discretely — the new
	// pair on one side of the flip is paired with a continuously-varying
	// weight, producing visible stair-steps at any boundary where adjacent
	// texels store different pairs. Doing the per-corner blend first and
	// then bilinear-interping the resulting colors removes that mismatch.
	ivec2 mapSize = textureSize(uSurfaceIndices, 0);
	vec2 pixelCoord = imUV * vec2(mapSize) - 0.5;
	ivec2 base = ivec2(floor(pixelCoord));
	vec2 f = fract(pixelCoord);
	ivec2 c00 = clamp(base + ivec2(0, 0), ivec2(0), mapSize - 1);
	ivec2 c10 = clamp(base + ivec2(1, 0), ivec2(0), mapSize - 1);
	ivec2 c01 = clamp(base + ivec2(0, 1), ivec2(0), mapSize - 1);
	ivec2 c11 = clamp(base + ivec2(1, 1), ivec2(0), mapSize - 1);

	// Fast-path detection: if all 4 corners hold the same (idxA, idxB) pair,
	// only their weight varies — one triplanar evaluation suffices. This is
	// the case for the vast majority of fragments (interior of any region or
	// uniform feather). Boundary fragments fall through to the 4-corner path.
	vec2 idxRG00 = texelFetch(uSurfaceIndices, c00, 0).rg;
	vec2 idxRG10 = texelFetch(uSurfaceIndices, c10, 0).rg;
	vec2 idxRG01 = texelFetch(uSurfaceIndices, c01, 0).rg;
	vec2 idxRG11 = texelFetch(uSurfaceIndices, c11, 0).rg;
	bool uniformIdx = all(equal(idxRG00, idxRG10)) &&
	                  all(equal(idxRG00, idxRG01)) &&
	                  all(equal(idxRG00, idxRG11));

	vec3 albedo;
	float roughness;
	vec3 outN;

	if (uniformIdx) {
		float w00 = texelFetch(uSurfaceWeight, c00, 0).r;
		float w10 = texelFetch(uSurfaceWeight, c10, 0).r;
		float w01 = texelFetch(uSurfaceWeight, c01, 0).r;
		float w11 = texelFetch(uSurfaceWeight, c11, 0).r;
		float w = mix(mix(w00, w10, f.x), mix(w01, w11, f.x), f.y);

		int idxA = int(idxRG00.r * 255.0 + 0.5);
		int idxB = int(idxRG00.g * 255.0 + 0.5);
		vec4 propsA = texelFetch(uSurfacePropsTex, ivec2(idxA, 0), 0);
		vec4 propsB = texelFetch(uSurfacePropsTex, ivec2(idxB, 0), 0);
		float tilingA = propsA.r;
		float tilingB = propsB.r;

		vec3 aA = sampleTriplanarAlbedo(float(idxA), worldPos, triW, tilingA);
		vec3 aB = sampleTriplanarAlbedo(float(idxB), worldPos, triW, tilingB);
		albedo = mix(aA, aB, w);

		float roughA = propsA.g;
		float roughB = propsB.g;
#ifdef NOODLE_HAS_ROUGHNESS_ARRAY
		roughA = sampleTriplanarRoughness(float(idxA), worldPos, triW, tilingA);
		roughB = sampleTriplanarRoughness(float(idxB), worldPos, triW, tilingB);
#endif
		roughness = mix(roughA, roughB, w);

		outN = N;
#ifdef NOODLE_HAS_NORMAL_ARRAY
		vec3 nA = sampleTriplanarNormal(float(idxA), worldPos, N, triW, tilingA);
		vec3 nB = sampleTriplanarNormal(float(idxB), worldPos, N, triW, tilingB);
		outN = normalize(mix(nA, nB, w));
#endif
	} else {
		TexelEval e00 = evalTexel(c00, worldPos, N, triW);
		TexelEval e10 = evalTexel(c10, worldPos, N, triW);
		TexelEval e01 = evalTexel(c01, worldPos, N, triW);
		TexelEval e11 = evalTexel(c11, worldPos, N, triW);

		albedo = mix(
			mix(e00.albedo, e10.albedo, f.x),
			mix(e01.albedo, e11.albedo, f.x),
			f.y
		);
		roughness = mix(
			mix(e00.roughness, e10.roughness, f.x),
			mix(e01.roughness, e11.roughness, f.x),
			f.y
		);
		outN = normalize(mix(
			mix(e00.normal, e10.normal, f.x),
			mix(e01.normal, e11.normal, f.x),
			f.y
		));
	}

	gAlbedo = vec4(albedo, 0.0);
	// Negative roughness is the terrain sentinel read by the SSAO pass to skip
	// SSAO on terrain surfaces. The deferred-light pass uses abs().
	gNormal = vec4(outN, -(roughness + 0.001));

	vec2 ndcCurr = vClipCurr.xy / vClipCurr.w;
	vec2 ndcPrev = vClipPrev.xy / vClipPrev.w;
	gMotion = (ndcPrev - ndcCurr) * 0.5;
}
`;

    private static vertexShaderSource = `
uniform mat4 uProjectionMatrix;
uniform mat4 uUnjitteredViewProjection;
uniform mat4 uPreviousViewProjection;

#ifdef NOODLE_INSTANCED
layout(location = 6) in vec4 aInstanceModelCol0;
layout(location = 7) in vec4 aInstanceModelCol1;
layout(location = 8) in vec4 aInstanceModelCol2;
layout(location = 9) in vec4 aInstanceModelCol3;
uniform mat4 uViewMatrix;
#else
uniform mat4 uModelViewMatrix;
uniform mat4 uModelMatrix;
#endif

layout(location = 0) in vec3 aVertexPosition;
layout(location = 1) in vec3 aVertexNormal;

out vec4 vWorldPosition;
out vec3 vWorldNormal;
out vec4 vClipCurr;
out vec4 vClipPrev;

void main(void) {
	vec4 pos = vec4(aVertexPosition, 1.0);
	vec4 normal = vec4(aVertexNormal, 0.0);

#ifdef NOODLE_INSTANCED
	mat4 instanceModel = mat4(aInstanceModelCol0, aInstanceModelCol1, aInstanceModelCol2, aInstanceModelCol3);
	vWorldPosition = instanceModel * vec4(pos.xyz, 1.0);
	vWorldNormal = normalize(mat3(instanceModel) * normal.xyz);
	gl_Position = uProjectionMatrix * uViewMatrix * vWorldPosition;
#else
	vWorldPosition = uModelMatrix * vec4(pos.xyz, 1.0);
	vWorldNormal = normalize(mat3(uModelMatrix) * normal.xyz);
	vec4 viewPos = uModelViewMatrix * vec4(pos.xyz, 1.0);
	gl_Position = uProjectionMatrix * viewPos;
#endif

	vClipCurr = uUnjitteredViewProjection * vWorldPosition;
	vClipPrev = uPreviousViewProjection * vWorldPosition;
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: GBufferTerrainShaderUniformsContainer;
    public attributesContainer: GBufferTerrainShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: GBufferTerrainShaderSettings) {
        super(ctx, GBufferTerrainShader.fragmentShaderSource, GBufferTerrainShader.vertexShaderSource, settings);
        this.uniformsContainer = new GBufferTerrainShaderUniformsContainer(ctx, this);
        this.attributesContainer = new GBufferTerrainShaderShaderAttributesContainer(ctx, this);
    }
}

class GBufferTerrainShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: GBufferTerrainShader) {
        super(ctx);
    }

    public setProjectionMatrix(value: mat4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uProjectionMatrix', this.shader.uniforms.uProjectionMatrix, value, 'mat4');
        return this;
    }

    public setUnjitteredViewProjection(value: mat4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uUnjitteredViewProjection', this.shader.uniforms.uUnjitteredViewProjection, value, 'mat4');
        return this;
    }

    public setPreviousViewProjection(value: mat4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uPreviousViewProjection', this.shader.uniforms.uPreviousViewProjection, value, 'mat4');
        return this;
    }

    public setViewMatrix(value: mat4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uViewMatrix', this.shader.uniforms.uViewMatrix, value, 'mat4');
        return this;
    }

    public setModelViewMatrix(value: mat4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uModelViewMatrix', this.shader.uniforms.uModelViewMatrix, value, 'mat4');
        return this;
    }

    public setModelMatrix(value: mat4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uModelMatrix', this.shader.uniforms.uModelMatrix, value, 'mat4');
        return this;
    }

    public setAlbedoArray(texture: Texture) : GBufferTerrainShaderUniformsContainer {
        this.setTextureValue('uAlbedoArray', this.shader.uniforms.uAlbedoArray, texture, 'sampler2DArray');
        return this;
    }

    public setNormalArray(texture: Texture) : GBufferTerrainShaderUniformsContainer {
        this.setTextureValue('uNormalArray', this.shader.uniforms.uNormalArray, texture, 'sampler2DArray');
        return this;
    }

    public setRoughnessArray(texture: Texture) : GBufferTerrainShaderUniformsContainer {
        this.setTextureValue('uRoughnessArray', this.shader.uniforms.uRoughnessArray, texture, 'sampler2DArray');
        return this;
    }

    public setSurfaceIndices(texture: Texture) : GBufferTerrainShaderUniformsContainer {
        this.setTextureValue('uSurfaceIndices', this.shader.uniforms.uSurfaceIndices, texture, 'sampler2D');
        return this;
    }

    public setSurfaceWeight(texture: Texture) : GBufferTerrainShaderUniformsContainer {
        this.setTextureValue('uSurfaceWeight', this.shader.uniforms.uSurfaceWeight, texture, 'sampler2D');
        return this;
    }

    public setIndexMapBoundsAndSize(value: vec4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uIndexMapBoundsAndSize', this.shader.uniforms.uIndexMapBoundsAndSize, value, 'vec4');
        return this;
    }

    public setSurfacePropsTex(texture: Texture) : GBufferTerrainShaderUniformsContainer {
        this.setTextureValue('uSurfacePropsTex', this.shader.uniforms.uSurfacePropsTex, texture, 'sampler2D');
        return this;
    }

    public setDitherFade(value: number) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uDitherFade', this.shader.uniforms.uDitherFade, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uAlbedoArray', uniform: this.shader.uniforms.uAlbedoArray, dataType: 'sampler2DArray' },
			{ name: 'uNormalArray', uniform: this.shader.uniforms.uNormalArray, dataType: 'sampler2DArray' },
			{ name: 'uRoughnessArray', uniform: this.shader.uniforms.uRoughnessArray, dataType: 'sampler2DArray' },
			{ name: 'uSurfaceIndices', uniform: this.shader.uniforms.uSurfaceIndices, dataType: 'sampler2D' },
			{ name: 'uSurfaceWeight', uniform: this.shader.uniforms.uSurfaceWeight, dataType: 'sampler2D' },
			{ name: 'uSurfacePropsTex', uniform: this.shader.uniforms.uSurfacePropsTex, dataType: 'sampler2D' }
        ];
    }
}

class GBufferTerrainShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: GBufferTerrainShader) {
        super(ctx);
    }

    public setInstanceModelCol0(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol0, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setInstanceModelCol1(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol1, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setInstanceModelCol2(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol2, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setInstanceModelCol3(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol3, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setVertexNormal(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexNormal, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldPosition(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldNormal(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldNormal, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvClipCurr(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vClipCurr, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvClipPrev(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vClipPrev, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}